/**
 * DeletionWarningBanner Component
 * Global banner shown when account is scheduled for deletion
 * GDPR Art. 17 compliant with grace period cancellation option
 */

import React, { useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useDeletionWarning } from '@/shared/contexts/DeletionWarningContext'
import { userConfigService } from '@/shared/services'

export const DeletionWarningBanner: React.FC = React.memo(function DeletionWarningBanner() {
  const { t } = useTranslation('shared')
  const {
    deletionRequest,
    daysRemaining,
    isCancelling,
    cancelDeletion,
    dismissTemporarily,
    isDismissed,
  } = useDeletionWarning()

  const [showConfirm, setShowConfirm] = useState(false)

  // Don't render if no active deletion or dismissed
  if (!deletionRequest || isDismissed) return null

  const handleCancel = async () => {
    const success = await cancelDeletion()
    if (success) {
      setShowConfirm(false)
    }
  }

  const formatDate = (dateString: string) =>
    // Honor the user's dateFormat preference (with time), not the browser locale.
    userConfigService.formatDate(dateString, true)

  // Determine urgency styling based on days remaining
  const isUrgent = daysRemaining !== null && daysRemaining <= 3
  const bgClass = isUrgent
    ? 'bg-gradient-to-r from-red-600 to-red-700'
    : 'bg-gradient-to-r from-amber-600 to-orange-600'

  return (
    <div className={`${bgClass} text-white px-4 py-3 shadow-lg z-50`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
        {/* Warning message */}
        <div className="flex items-center gap-3">
          {/* Warning icon */}
          <div className={`flex-shrink-0 ${isUrgent ? 'animate-pulse' : ''}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <div>
            <p className="font-semibold">
              {isUrgent ? t('deletionWarning.title') : t('deletionWarning.scheduled')}
            </p>
            <p className="text-sm text-white/90">
              {daysRemaining === 0 ? (
                <span className="font-bold">{t('deletionWarning.today')}</span>
              ) : daysRemaining === 1 ? (
                <span>{t('deletionWarning.tomorrow')}</span>
              ) : (
                <>
                  {t('deletionWarning.daysRemaining', { count: daysRemaining })} •{' '}
                  {t('deletionWarning.scheduledFor', {
                    date: formatDate(deletionRequest.scheduledFor),
                  })}
                </>
              )}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!showConfirm ? (
            <>
              <button
                onClick={() => setShowConfirm(true)}
                className="px-4 py-1.5 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                {t('deletionWarning.cancelButton')}
              </button>
              <button
                onClick={dismissTemporarily}
                className="p-1.5 text-white/70 hover:text-white transition-colors"
                title={t('deletionWarning.hideTemporarily')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </>
          ) : (
            <>
              <span className="text-sm mr-2">{t('deletionWarning.confirmTitle')}</span>
              <button
                onClick={handleCancel}
                disabled={isCancelling}
                className="px-4 py-1.5 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
              >
                {isCancelling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('deletionWarning.canceling')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {t('deletionWarning.keepAccount')}
                  </>
                )}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isCancelling}
                className="px-4 py-1.5 bg-white/20 text-white font-medium rounded-lg hover:bg-white/30 transition-colors text-sm disabled:opacity-50"
              >
                {t('deletionWarning.no')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
})
